import React, { useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import { StyledButton } from '../../../globalStyled';
import {
  StyledPopUp,
  StyledPopUpBlock,
  StyledCross,
  StyledPopUpContainer,
  StyledButtonContainer,
  StyledHeader,
  StyledParagraph,
  StyledLoader,
  StyledRangeSelect,
  StyledRangeLabel,
  StyledPopupButton
} from '../styled';
import { hidePopupWindow } from '../../../redux/actions/Popups';
import { transfersListRequest } from '../../../redux/actions/Transfers';
import { createNewContractOffer, extendExistingContractOffer } from '../../../redux/actions/Contracts';

import { getPopupByName } from '../../../redux/selector/Popups';

/** Suggested starting daily wage (current weekly salary → daily, slight bump). */
const suggestedDailyWage = (salary) => {
  const n = Number(salary);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.floor(n / 7) + 10;
};

const WAGE_FLOOR = 1;

const ContractOfferPopup = ({ popupName, id, isExtended, isFreeAgentPopup, salary }) => {
  const salaryRef = React.useRef(salary);
  if (salary != null && Number.isFinite(Number(salary))) {
    salaryRef.current = salary;
  }
  const suggestedWage = suggestedDailyWage(salaryRef.current);
  const wageStep = suggestedWage > 0
    ? Math.max(1000, Math.floor(suggestedWage / 10))
    : 1000;
  const [step, changeStep] = useState('contract');
  const [wage, setWage] = useState(suggestedWage);
  const [seasonsAmount, setSeasonsAmount] = useState(1);
  const [matchesAmount, setMatchesAmount] = useState(1);
  const [isFreeAgentInfo, showFreeAgentInfo] = useState(isFreeAgentPopup);
  const wageReady = React.useRef(suggestedWage > 0);
  React.useEffect(() => {
    if (!wageReady.current && suggestedWage > 0) {
      setWage(suggestedWage);
      wageReady.current = true;
    }
  }, [suggestedWage]);
  // TODO break out to constants
  const steps = {
    contract: 'contract',
    complete: 'complete',
    error: 'error',
  };
  const dispatch = useDispatch();

  const popupState = useSelector(getPopupByName(popupName));
  const { isLoading, error } = popupState || {};

  React.useEffect(() => {
    if (!isEmpty(error)) {
      changeStep(steps.error);
    }
  }, [error, steps.error]);

  const moveToComplete = () => {
    const payload = {
      player: id,
      wage: parseInt(wage, 10),
      playTime: parseInt(matchesAmount, 10),
      length: parseInt(seasonsAmount, 10),
    };
    if (isExtended) {
      dispatch(extendExistingContractOffer(payload));
    } else {
      dispatch(createNewContractOffer(payload));
    }
    changeStep(steps.complete);
  };

  const hidePopup = () => {
    if (!isLoading) {
      dispatch(hidePopupWindow(popupName));
      dispatch(transfersListRequest());
    }
  };

  const contractTitle = () => {

    switch (true) {
      case isExtended && !isFreeAgentPopup: return 'Contract Extension';
      case isFreeAgentPopup && !isExtended: return 'Free agent contract'
      default: return 'New contract'
    }
  }
  return (
    <StyledPopUp>
      <StyledPopUpBlock maxWidth={375}>
        <StyledCross onClick={() => hidePopup()} />
        <StyledPopUpContainer>
          {isLoading ? (
            <StyledLoader
              type="Bars"
              color="var(--tol-header-accent)"
              height={100}
              width={100}
            />
          ) : (
            <>
              {step === steps.error && (
                <>
                  <StyledHeader> {error.header}</StyledHeader>
                  <StyledParagraph>{error.text}</StyledParagraph>
                </>
              )}
              {step === steps.contract && (
                isFreeAgentInfo ?
                  <>
                    <StyledHeader>Free agent fee</StyledHeader>
                    <StyledParagraph>
                      There is a $250,000 league fee for signing a free agent.
                    </StyledParagraph>
                    <StyledParagraph>Would you like to proceed?</StyledParagraph>
                  </>
                  :
                  <>
                    <StyledHeader>{contractTitle()}</StyledHeader>
                    <StyledRangeLabel>
                      Daily wage
                    </StyledRangeLabel>
                    <StyledRangeSelect
                      prefix="$"
                      value={wage}
                      callback={setWage}
                      minValue={WAGE_FLOOR}
                      maxValue={Math.max(suggestedWage * 5, wageStep * 5)}
                      stepAmount={wageStep}
                    />
                    <StyledRangeLabel>
                      Contract length
                    </StyledRangeLabel>
                    <StyledRangeSelect
                      stepAmount={1}
                      minValue={1}
                      maxValue={5}
                      value={seasonsAmount}
                      callback={setSeasonsAmount}
                      thousandSeparator={false}
                      suffix={seasonsAmount > 1 ? " seasons" : " season"}
                      placeholder='0 seasons'
                    />
                    <StyledRangeLabel>
                      Play time
                    </StyledRangeLabel>
                    <StyledRangeSelect
                      thousandSeparator={false}
                      stepAmount={1}
                      minValue={1}
                      maxValue={25}
                      value={matchesAmount}
                      callback={setMatchesAmount}
                      placeholder="0 matches"
                      suffix={matchesAmount > 1 ? " matches" : " match"} />
                  </>
              )}
              {step === steps.complete && !error && (
                <>
                  <StyledHeader>Contract sent</StyledHeader>
                  <StyledParagraph>
                    Please wait for response
                  </StyledParagraph>
                </>
              )}
              <StyledButtonContainer>
                {step === steps.contract && (
                  <>
                    <StyledPopupButton
                      type="button"
                      cancel
                      small
                      onClick={() => hidePopup()}>
                      Cancel
                    </StyledPopupButton>
                    {isFreeAgentInfo ?
                      <StyledPopupButton
                        type="button"
                        primary
                        onClick={() => showFreeAgentInfo(false)}
                      >
                        Proceed
                      </StyledPopupButton>
                      :
                      <StyledPopupButton
                        type="button"
                        disabled={
                          seasonsAmount < 1 ||
                          seasonsAmount > 5 ||
                          matchesAmount < 1 ||
                          matchesAmount > 25 ||
                          !Number.isFinite(Number(wage)) ||
                          Number(wage) < WAGE_FLOOR
                        }
                        primary
                        onClick={moveToComplete}
                      >
                        Make Offer
                      </StyledPopupButton>}
                  </>
                )}

                {(step === steps.error || step === steps.complete) && (
                  <StyledButton primary onClick={() => hidePopup()}>
                    Got it!
                  </StyledButton>
                )}
              </StyledButtonContainer>
            </>
          )}
        </StyledPopUpContainer>
      </StyledPopUpBlock>
    </StyledPopUp>
  );
};

export default React.memo(ContractOfferPopup);
